import {
  createMeAddressV1,
  deleteMeAddressV1,
  getMeAddressesV1,
  setMeAddressPreferredV1,
  updateMeAddressV1,
} from '../../api/me-—-address-book/me-—-address-book';
import { AddressBookDto, SaveAddressRequest, SetPreferredAddressRequest } from '../../api/model';

/**
 * Fetches the address book for the authenticated user.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `AddressBookDto[]` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getAddresses(language?: string): Promise<AddressBookDto[] | undefined> {
  try {
    const response = await getMeAddressesV1({ headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 200) {
      return response.data as AddressBookDto[];
    }

    console.warn(`getAddresses: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getAddresses: request failed:', error);
    return undefined;
  }
}

/**
 * Creates a new address book entry for the authenticated user.
 *
 * If `isPreferredDelivery` or `isPreferredBilling` is `true`, the corresponding
 * flag is cleared from any existing address before the new one is saved, and the
 * entry is synced to MuleSoft automatically by the API.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param data - The address payload to create.
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `AddressBookDto` on HTTP 201.
 * @returns `undefined` on 400 (validation error), 401 (unauthenticated), or network error.
 */
export async function createAddress(
  data: SaveAddressRequest,
  language?: string
): Promise<AddressBookDto | undefined> {
  try {
    const response = await createMeAddressV1(data, { headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 201) {
      return response.data as AddressBookDto;
    }

    console.warn(`createAddress: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('createAddress: request failed:', error);
    return undefined;
  }
}

/**
 * Updates an existing address book entry for the authenticated user.
 *
 * If `isPreferredDelivery` or `isPreferredBilling` is `true`, the corresponding
 * flag is cleared from any existing address before the update is saved, and the
 * entry is synced to MuleSoft automatically by the API.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The UUID of the address to update.
 * @param data - The updated address payload.
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `AddressBookDto` on HTTP 200.
 * @returns `undefined` on 400 (validation error), 401 (unauthenticated), 404 (not found), or network error.
 */
export async function updateAddress(
  id: string,
  data: SaveAddressRequest,
  language?: string
): Promise<AddressBookDto | undefined> {
  try {
    const response = await updateMeAddressV1(id, data, { headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 200) {
      return response.data as AddressBookDto;
    }

    console.warn(`updateAddress: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('updateAddress: request failed:', error);
    return undefined;
  }
}

/**
 * Soft-deletes an address book entry for the authenticated user.
 *
 * Preferred delivery and billing flags are cleared on the deleted entry.
 * No MuleSoft sync is triggered by this operation.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The UUID of the address to delete.
 * @returns `true` on HTTP 204.
 * @returns `false` on 401 (unauthenticated), 404 (not found), or network error.
 */
export async function deleteAddress(id: string): Promise<boolean> {
  try {
    const response = await deleteMeAddressV1(id);

    if (response.status === 204) {
      return true;
    }

    console.warn(`deleteAddress: unexpected status ${response.status}`);
    return false;
  } catch (error) {
    console.warn('deleteAddress: request failed:', error);
    return false;
  }
}

/**
 * Marks an address book entry as the preferred delivery and/or billing address.
 *
 * The corresponding flag is cleared from any other address before this one is
 * saved. The affected address block(s) (shippingAddress / billingAddress) are
 * synced to MuleSoft automatically by the API.
 *
 * At least one of `delivery` or `billing` must be `true`.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The UUID of the address to mark as preferred.
 * @param data - Flags indicating which preference(s) to set (`delivery`, `billing`).
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `AddressBookDto` on HTTP 200.
 * @returns `undefined` on 400 (neither flag set), 401 (unauthenticated), 404 (not found), or network error.
 */
export async function setPreferredAddress(
  id: string,
  data: SetPreferredAddressRequest,
  language?: string
): Promise<AddressBookDto | undefined> {
  try {
    const response = await setMeAddressPreferredV1(id, data, {
      headers: { 'Accept-Language': language ?? '' },
    });

    if (response.status === 200) {
      return response.data as AddressBookDto;
    }

    console.warn(`setPreferredAddress: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('setPreferredAddress: request failed:', error);
    return undefined;
  }
}
